import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { API } from 'network/api';
import useSeasonIndex from 'hooks/useSeasonIndex';
import { selectUserCountry } from 'store/auth/selectors';

export interface PgData {
  id: string;
  seasonName?: string;
  almType?: string;
  estimateStartDate?: string;
  estimateEndDate?: string;
  seasonId?: string;
}

export interface SeasonOption {
  label: string;
  value: string;
  pgData: PgData[];
}

interface UseFarmerPgsParams {
  farmerId?: string;
}

const fetchPgData = async ({
  farmerId,
  seasonTypeId,
  country,
}: {
  farmerId: string;
  seasonTypeId: string;
  country: string;
}): Promise<PgData[]> => {
  const response = await API.get(
    `/season/farmer/?country.equals=${country}&farmerId.equals=${farmerId}&seasonTypeId.equals=${seasonTypeId}`,
  );
  return response?.data?.content || [];
};

const useFarmerPgs = ({ farmerId }: UseFarmerPgsParams) => {
  const country = useSelector(selectUserCountry);
  const {
    seasonIndexFilterOptions,
    currentSeason,
    isLoading: isSeasonLoading,
  } = useSeasonIndex(country);

  const [selectedSeason, setSelectedSeason] = useState<
    SeasonOption | undefined
  >(
    currentSeason
      ? { label: currentSeason.seasonCode, value: currentSeason.id, pgData: [] }
      : undefined,
  );

  useMemo(() => {
    if (
      currentSeason?.seasonCode &&
      currentSeason?.id
    ) {
      setSelectedSeason({
        label: currentSeason.seasonCode,
        value: currentSeason.id,
        pgData: [],
      });
    }
  }, [currentSeason?.seasonCode, currentSeason?.id]);

  const { data: pgData = [], isLoading: isPgLoading } = useQuery({
    queryKey: ['pgData', farmerId, selectedSeason?.value, country],
    queryFn: () =>
      fetchPgData({
        farmerId: farmerId!,
        seasonTypeId: selectedSeason!.value,
        country: country!,
      }),
    enabled: !!farmerId && !!selectedSeason?.value && !!country,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  const pgOptions = useMemo<SeasonOption[]>(
    () =>
      (seasonIndexFilterOptions || []).map((item) => ({
        label: item.label,
        value: item.value,
        pgData: selectedSeason?.label === item.label ? pgData : [],
      })),
    [seasonIndexFilterOptions, selectedSeason?.label, pgData],
  );

  return {
    loading: isSeasonLoading || isPgLoading,
    pgOptions,
    selectedSeason,
    setSelectedSeason,
    currentSeason: currentSeason?.seasonCode,
  };
};

export default useFarmerPgs;
